/**
 * Project View Tracking Utility
 * 
 * Stores and retrieves last viewed timestamps for projects in localStorage.
 * This allows sorting projects by most recently viewed on the client side.
 */

const STORAGE_KEY = "projectLastViewed";

/**
 * Get all project view timestamps from localStorage
 * @returns {Object} Map of projectId -> timestamp (ISO string)
 */
export const getProjectViewTimestamps = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error("Error reading project view timestamps from localStorage:", error);
    return {};
  }
};

/**
 * Record that a project was viewed now
 * @param {string} projectId - The project ID
 */
export const recordProjectView = (projectId) => {
  if (!projectId) return;
  
  try {
    const timestamps = getProjectViewTimestamps();
    timestamps[projectId] = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(timestamps));
  } catch (error) {
    console.error("Error recording project view in localStorage:", error);
  }
};

/**
 * Get the last viewed timestamp for a specific project
 * @param {string} projectId - The project ID
 * @returns {Date|null} The last viewed date, or null if never viewed
 */
export const getProjectLastViewed = (projectId) => {
  if (!projectId) return null;
  
  const timestamps = getProjectViewTimestamps();
  const timestamp = timestamps[projectId];
  return timestamp ? new Date(timestamp) : null;
};

/**
 * Clear all project view tracking data
 */
export const clearProjectViewTracking = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing project view tracking:", error);
  }
};
